// Phase 9 — breadcrumbs rendered immediately under the sticky header on
// every authenticated page (except login / register / access-request,
// which don't mount AppShell anyway).
//
// Format: GROUP_LABEL > Section > Current Page. Each crumb except the
// last is a router <Link>. Derived automatically from the route-meta
// table in /app/frontend/src/lib/routeMeta.js.
import { Link, useLocation, useParams } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { fillPath, groupById, matchRoute, routeById } from "@/lib/routeMeta";

export default function Breadcrumbs() {
    const { pathname } = useLocation();
    const params = useParams();

    const route = matchRoute(pathname);
    if (!route) return null;

    // Build the crumb chain: group label (if any) → parent chain → current.
    const crumbs = [];
    const group = route.group ? groupById(route.group) : null;
    if (group) {
        crumbs.push({
            key: `g-${group.id}`,
            label: group.label,
            to: null, // group headings aren't clickable — they're headings.
            muted: true,
        });
    }

    // Walk the parent chain (at most a few levels deep).
    const chain = [];
    let cursor = route.parent ? routeById(route.parent) : null;
    let guard = 0;
    while (cursor && guard < 5) {
        chain.unshift(cursor);
        cursor = cursor.parent ? routeById(cursor.parent) : null;
        guard += 1;
    }
    for (const r of chain) {
        crumbs.push({
            key: r.id,
            label: r.label,
            to: fillPath(r.path, params),
        });
    }

    // Current crumb — may carry a :id-style parameter we shorten for legibility.
    const currentLabel = currentCrumbLabel(route, params);
    crumbs.push({
        key: route.id,
        label: currentLabel,
        to: null, // last crumb is never a link.
        current: true,
    });

    return (
        <nav
            className="flex items-center gap-1 px-4 sm:px-6 lg:px-8 py-2 border-b border-ux-border-1 bg-ux-bg-1"
            aria-label="Breadcrumb"
            data-testid="breadcrumbs"
        >
            <ol className="flex items-center gap-1 text-[12px] tabular-nums overflow-x-auto">
                {crumbs.map((c, idx) => (
                    <li key={c.key} className="flex items-center gap-1 shrink-0">
                        {idx > 0 && (
                            <ChevronRight
                                className="w-3 h-3 text-ux-text-3 shrink-0"
                                aria-hidden
                            />
                        )}
                        {c.to ? (
                            <Link
                                to={c.to}
                                className="text-ux-text-2 hover:text-ux-text-1 transition-colors"
                                data-testid={`breadcrumb-${c.key}`}
                            >
                                {c.label}
                            </Link>
                        ) : (
                            <span
                                className={
                                    c.current
                                        ? "text-ux-text-1 font-medium"
                                        : "text-ux-text-3 uppercase tracking-[0.15em] text-[11px]"
                                }
                                data-testid={`breadcrumb-${c.key}`}
                                aria-current={c.current ? "page" : undefined}
                            >
                                {c.label}
                            </span>
                        )}
                    </li>
                ))}
            </ol>
        </nav>
    );
}

function currentCrumbLabel(route, params) {
    // For deep-link pages that carry an id, show a shortened id tail so
    // operators can recognise the record (e.g. 01KQJR…NRN2) without the
    // crumb eating half the header.
    if (params?.id && route.path.includes(":id")) {
        const id = params.id;
        const tail = id.length > 12 ? `${id.slice(0, 4)}…${id.slice(-4)}` : id;
        return `${route.label} — ${tail}`;
    }
    if (params?.family && params?.name && route.path.includes(":family")) {
        return `${route.label} — ${params.family}/${params.name}`;
    }
    return route.pageTitle || route.label;
}
